import React, { useState, Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import Navbar from './components/Navbar';
import CartDrawer from './components/CartDrawer/CartDrawer';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import 'boxicons/css/boxicons.min.css';
import { LoginModalProvider } from './context/LoginModalWrapper';
import ScrollToTop from './components/ScrollToTop';
import { SearchProvider } from './context/SearchContext';
import { PopupProvider } from './context/PopupContext';

// ✅ Lazy-loaded Pages
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/AllProducts'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const Cart = lazy(() => import('./pages/Cart'));
const Profile = lazy(() => import('./pages/Profile'));
const Login = lazy(() => import('./components/LoginModal'));
const Register = lazy(() => import('./components/Register'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutUs = lazy(() => import('./pages/Aboutus'));
const Checkout = lazy(() => import('./pages/Checkout'));

// ✅ Lazy-loaded Admin Pages
const Admin = lazy(() => import('./Admin/AdminPage'));
const AdminProductList = lazy(() => import('./Admin/AdminProductList'));
const AdminAddProduct = lazy(() => import('./Admin/AdminAddProduct'));
const AdminComboList = lazy(() => import('./Admin/AdminComboList'));
const AdminAddCombos = lazy(() => import('./Admin/AdminAddCombos'));
const AdminOrders = lazy(() => import('./Admin/AdminOrders'));
const AdminMessages = lazy(() => import('./Admin/AdminMessages'));
const AdminOffers = lazy(() => import('./Admin/AdminOffers'));
const AdminBannerManager = lazy(() => import('./Admin/AdminBannerManager'));
const AdminHighlights = lazy(() => import('./Admin/AdminHighlights'));


import LogoLoader from './components/LogoLoader';

import useAnalytics from './useAnalytics';


function App() {
  const [isCartOpen, setCartOpen] = useState(false);
  useAnalytics();
  return (
    <AuthProvider>
      <PopupProvider>
        <CartProvider>
          <LoginModalProvider>
            <SearchProvider>
              <ScrollToTop />
              <div className="flex flex-col min-h-screen relative">
                {/* ✅ Optional Navbar Lazy Load */}
                {/* <Navbar onCartClick={() => setCartOpen(true)} /> */}

                <div className="flex-1">
                  <Suspense fallback={<LogoLoader />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/products" element={<Products />} />
                      <Route path="/product/:id" element={<ProductPage />} />
                      <Route path="/combo/:id" element={<ProductPage />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/about" element={<AboutUs />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/checkout" element={<Checkout />} />

                      {/* ✅ Admin Routes */}
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute adminOnly={true}>
                            <Admin />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="products" element={<AdminProductList />} />
                        <Route path="combos" element={<AdminComboList />} />
                        <Route path="orders" element={<AdminOrders />} />
                        <Route path="addproduct" element={<AdminAddProduct />} />
                        <Route path="addcombos" element={<AdminAddCombos />} />
                        <Route path="messages" element={<AdminMessages />} />
                        <Route path="offers" element={<AdminOffers />} />
                        <Route path="banner" element={<AdminBannerManager />} />
                        <Route path="highlights" element={<AdminHighlights />} />
                      </Route>
                    </Routes>
                  </Suspense>
                </div>

                {isCartOpen && <CartDrawer onClose={() => setCartOpen(false)} />}
              </div>
            </SearchProvider>
          </LoginModalProvider>
        </CartProvider>
      </PopupProvider>
    </AuthProvider>
  );
}

export default App;
